import React, { useCallback, useEffect, useReducer, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { useParams } from "react-router-dom";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { withContext } from "../context";
import { TEMPLATES } from "../constants";
import logger from "../logger";
import { listProducts } from "../graphql/queries";
import Loading from "../components/Loading";
import { HomeDefault } from "../templates/default";
import { HomeEcommerce } from "../templates/ecommerce";
import NotAvailable from "./NotAvailable";

const initialState = {
  fetchingProducts: true,
  products: [],
  grouped: [],
  items: [],
  totalPrice: 0,
  totalItems: 0
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "fetchingProducts":
    case "items":
    case "totalPrice":
    case "totalItems":
      return { ...state, [type]: payload };
    case "products":
      return {
        ...state,
        products: payload.products,
        grouped: payload.grouped
      };
    case "updateItems":
      return {
        ...state,
        items: payload.items,
        totalPrice: payload.totalPrice,
        totalItems: payload.totalItems
      };
    default:
      return { ...state };
  }
};

const Home = ({ store, template, history, useStoreSlug }) => {
  const { params } = useParams();
  const mobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [state, dispatch] = useReducer(reducer, initialState);
  const [openPayment, setOpenPayment] = useState(false);

  const fetchProducts = useCallback(async () => {
    dispatch({ type: "fetchingProducts", payload: true });

    try {
      const allProducts = [];
      const featuredProducts = [];
      let next = null;

      do {
        const vars = {
          storeID: store.id,
          status: {
            eq: 1
          },
          nextToken: next
        };

        const result = await API.graphql(graphqlOperation(listProducts, vars));

        if (result.data.productsByStore) {
          for (let i = 0; i < result.data.productsByStore.items.length; i++) {
            const product = result.data.productsByStore.items[i];
            const body = JSON.parse(product.body);

            allProducts.push({ ...product, body });

            body.featured && featuredProducts.push({ ...product, body });
          }

          next = result.data.productsByStore.nextToken;
        }
      } while (Boolean(next));

      const grouped = [];

      for (let i = 0; i < store.body.categories.length; i++) {
        const category = store.body.categories[i];
        const products = [];

        for (let j = 0; j < allProducts.length; j++) {
          const product = allProducts[j];

          if (product.body.category === category.value) {
            products.push(product);
          }
        }

        if (products.length > 0) {
          grouped.push({ ...category, products });
        }
      }

      featuredProducts.length > 0 &&
        grouped.unshift({
          label: "Destacados",
          value: Date.now(),
          products: featuredProducts
        });

      dispatch({
        type: "products",
        payload: { products: allProducts, grouped }
      });
    } catch (err) {
      logger("Home.fetchingProducts.error", err);
    }

    dispatch({ type: "fetchingProducts", payload: false });
  }, [store]);

  useEffect(() => {
    if (store) {
      fetchProducts();
    }
  }, [store, fetchProducts]);

  useEffect(() => {
    if (params && state.products.length > 0) {
      try {
        const decoded = JSON.parse(atob(params));

        if (decoded.item) {
          const { productID, group, quantity, features } = decoded.item;

          for (let i = 0; i < state.products.length; i++) {
            const product = state.products[i];

            if (product.id === productID) {
              const item = {
                productID: product.id,
                price: product.price,
                quantity,
                total: product.price * quantity,
                body: { features },
                group,
                product
              };

              const payload = {
                items: [item],
                totalItems: item.quantity,
                totalPrice: item.total
              };

              dispatch({
                type: "updateItems",
                payload
              });
            }
          }
        }

        if (decoded.checkout) {
          setOpenPayment(true);
        }
      } catch (err) {
        logger("Home.decodeParams.error", err);
      }
    }
  }, [state.products, params]);

  const updateItems = (action, item, index) => {
    let items = [];
    let totalItems = 0;
    let totalPrice = 0;

    const before = index >= 0 ? state.items.slice(0, index) : null;
    const after = index >= 0 ? state.items.slice(index + 1, state.items.length) : null;

    switch (action) {
      case "create":
        items = [...state.items, item];
        break;
      case "update":
        items = [...before, item, ...after];
        break;
      case "delete":
        items = [...before, ...after];
        break;
      default:
        break;
    }

    for (let i = 0; i < items.length; i++) {
      totalPrice += items[i].total;
      totalItems += items[i].quantity;
    }

    const payload = {
      items,
      totalItems,
      totalPrice
    };

    dispatch({
      type: "updateItems",
      payload
    });
  };

  const paymentCompleted = url => {
    history.push(`${useStoreSlug ? `/${store.slug}` : ""}/${url}`);
  };

  if (!store || state.fetchingProducts) {
    return <Loading fullscreen />;
  }

  if (store && store.status === 0) {
    return <NotAvailable />;
  }

  if (!mobile && template === TEMPLATES.ecommerce) {
    return (
      <HomeEcommerce
        data={state}
        openPayment={openPayment}
        onOpenPayment={setOpenPayment}
        onUpdateItems={updateItems}
        onPaymentCompleted={paymentCompleted}
      />
    );
  } else {
    return (
      <HomeDefault
        mobile={mobile}
        data={state}
        openPayment={openPayment}
        onOpenPayment={setOpenPayment}
        onUpdateItems={updateItems}
        onPaymentCompleted={paymentCompleted}
      />
    );
  }
};

export default withContext(Home);
